import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { SeoPageView } from "@/components/seo-page-view";
import { airportByLowerCode, cityFromGen, cityToGen, fixPolishGrammar } from "@/lib/seo-routes";
import { buildSeoPageHead } from "@/lib/seo-page-loader";
import { countrySlug } from "@/lib/airports";
import { computeDefaultRouteDates } from "@/lib/route-dates";
import { buildSearchHref } from "@/lib/search-href";
import { supabase } from "@/integrations/supabase/client";
import { setEdgeCache } from "@/lib/ssr-cache";
import { faqScript } from "@/lib/faq-schema";
import { isValidFlightRoute, flightEndpointLabel } from "@/lib/flight-route-policy";
import { curatedRouteContent } from "@/lib/curated-flight-content";

export const Route = createFileRoute("/loty/$from/$to")({
  // SWR cache: dane trasy zmieniają się rzadko (treść SEO + statyczne lotniska),
  // a domyślne daty (+30/+37) i tak są wyliczane per render po stronie klienta.
  // 5 min świeżości → natychmiastowe re-rendery przy nawigacji wstecz/preload,
  // 30 min gc → trzymamy popularne trasy w pamięci podczas sesji.
  staleTime: 5 * 60 * 1000,
  gcTime: 30 * 60 * 1000,
  // Preload (hover/intent) traktujemy jak świeży przez 5 min, żeby nie strzelać
  // do Supabase za każdym razem, gdy użytkownik najedzie na ten sam link.
  shouldReload: false,
  loader: async ({ params }) => {
    const af = airportByLowerCode(params.from);
    const at = airportByLowerCode(params.to);
    if (!af || !at) throw notFound();
    // Aviasales nie wyszukuje po krajach — trasy z/do „cały kraj" (np. /loty/war/xgb)
    // przekierowujemy na stronę kraju albo stronę miasta wylotu.
    if (at.isCountry) {
      throw redirect({ to: "/loty/kraj/$slug", params: { slug: countrySlug(at.country) }, statusCode: 301 });
    }
    if (af.isCountry) {
      throw redirect({ to: "/loty/$city", params: { city: at.code.toLowerCase() }, statusCode: 301 });
    }
    if (!isValidFlightRoute(af.code, at.code)) throw notFound();
    // WAR and WAW are sent to Aviasales as the same city code; keep one
    // public route URL without merging the distinct Modlin airport (WMI).
    if (af.code === "WAR" || at.code === "WAR") {
      throw redirect({ to: "/loty/$from/$to", params: {
        from: (af.code === "WAR" ? "WAW" : af.code).toLowerCase(),
        to: (at.code === "WAR" ? "WAW" : at.code).toLowerCase(),
      }, statusCode: 301 });
    }
    // Edge HTML cache: 1h świeże, 24h SWR. Treść SEO + ceny route_prices
    // zmieniają się rzadko, Cloudflare serwuje z cache bez SSR + Supabase.
    await setEdgeCache();
    const [page, priceRow] = await Promise.all([
      Promise.resolve(curatedRouteContent(af, at)),
      // Live cena z cache route_prices — używana w JSON-LD Offer.lowPrice
      // jako rich snippet (cena „od" w SERP). Fallback do heurystyki bestMonth.
      (async () => {
        try {
          const { data } = await supabase
            .from("route_prices")
            .select("display_price,checked_at")
            .eq("from_iata", af.code)
            .eq("to_iata", at.code)
            .maybeSingle();
          return data ?? null;
        } catch {
          return null;
        }
      })(),
    ]);
    const { dd, rd } = computeDefaultRouteDates();
    return { af, at, page, dd, rd, priceRow };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [] };
    const { af, at, page, priceRow } = loaderData;
    const fallbackTitle =
      `Loty ${af.city} – ${at.city} (${af.code}–${at.code}) | Tanie Latanie`;
    const fallbackDesc =
      `Tanie bilety lotnicze ${af.city} → ${at.city} (${af.code}–${at.code}). ` +
      `Porównaj daty, lotniska, przesiadki i warunki bagażu przed rezerwacją.`;

    const canonicalPath = `/loty/${af.code.toLowerCase()}/${at.code.toLowerCase()}`;
    const head = buildSeoPageHead(page, canonicalPath, fallbackTitle, fallbackDesc);

    // Dynamiczny share-image (Twitter/Slack/Discord/LinkedIn). Meta dedupuje
    // po property=og:image — nasza wartość wygra nad domyślną z buildMeta.
    const ogImage = `https://tanielatanie.pl/og/route/${af.code.toLowerCase()}/${at.code.toLowerCase()}`;
    const dynamicImageMeta = [
      { property: "og:image", content: ogImage },
      { property: "og:image:type", content: "image/svg+xml" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: `Tanie loty ${af.city} → ${at.city}` },
      { name: "twitter:image", content: ogImage },
    ];
    const mergedMeta = [...(head.meta ?? []), ...dynamicImageMeta];

    const llmFaq = page?.faq ?? [];
    const faqLd = faqScript(llmFaq);

    const otherScripts = (head.scripts ?? []).filter((s) => {
      try {
        const t = JSON.parse(s.children)?.["@type"];
        return t !== "FAQPage" && t !== "BreadcrumbList";
      } catch { return true; }
    });

    const breadcrumbScript = {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Strona główna", item: "https://tanielatanie.pl/" },
          { "@type": "ListItem", position: 2, name: "Loty", item: "https://tanielatanie.pl/loty" },
          { "@type": "ListItem", position: 3, name: fixPolishGrammar(`Loty z ${cityFromGen(af.city)}`), item: `https://tanielatanie.pl/loty/${af.code.toLowerCase()}` },
          { "@type": "ListItem", position: 4, name: `${af.city} → ${at.city}`, item: `https://tanielatanie.pl${canonicalPath}` },
        ],
      }),
    };

    // Schema Trip + AggregateOffer (poniżej) niesie wszystkie potrzebne dane
    // cenowe do rich result. Schemat `Product` został usunięty, bo Google
    // wymagał dla niego `review`/`aggregateRating`, a dorabianie fałszywych
    // ocen łamie politykę Google (ryzyko manual action). Bilet lotniczy to
    // nie produkt — Trip jest semantycznie poprawniejszy.

    const tripScript = {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Trip",
        name: `Lot ${af.city} → ${at.city}`,
        description: `Kierunek ${flightEndpointLabel(af)} → ${flightEndpointLabel(at)}. Dostępność połączenia sprawdź dla wybranych dat.`,
        url: `https://tanielatanie.pl${canonicalPath}`,
        itinerary: [
          { "@type": "Airport", name: af.city, iataCode: af.code, address: { "@type": "PostalAddress", addressCountry: af.country } },
          { "@type": "Airport", name: at.city, iataCode: at.code, address: { "@type": "PostalAddress", addressCountry: at.country } },
        ],
      }),
    };

    const allScripts = [...otherScripts, breadcrumbScript, tripScript];
    if (faqLd) allScripts.push(faqLd);
    return { ...head, meta: mergedMeta, scripts: allScripts };
  },
  component: RoutePage,
});

function RoutePage() {
  const { af, at, page, dd, rd } = Route.useLoaderData();
  return (
    <SeoPageView
      type="route"
      from={af.code}
      to={at.code}
      fromName={af.city}
      toName={at.city}
      page={page}
      dd={dd}
      rd={rd}
      adults={1}
      fallbackH1={`Loty ${flightEndpointLabel(af)} → ${flightEndpointLabel(at)}`}
      searchHref={buildSearchHref({ from: af.code, to: at.code, dd, rd, adults: 1 })}
    />
  );
}
